// apps/web/src/components/DataReconfirmation/DataReconfirmationModal.tsx
//
// Blocking identity-reconfirmation modal. Shows the user's registration data
// (reusing the self-service Profile sections) so they can review and, if
// needed, edit it inline via the existing /profile/* endpoints. The document
// (CPF/CNPJ) renders read-only — that constraint is enforced by the sections
// themselves (PersonSection/OrganizationSection), whose self-service DTOs omit
// the document. A single "Confirmar dados" CTA stamps the confirmation.
//
// AlertDialog is the right primitive: blocking by design — no backdrop dismiss,
// ESC is inert (the dialog is controlled `open` with no onOpenChange), and no
// close (X) button is rendered — so the user cannot leave without confirming.
//
// Presentational only: the provider owns the open state and the confirm
// mutation. PF vs PJ is handled by the sections, which each self-gate on their
// own GET returning 200 (PersonSection for PF/CLT, OrganizationSection for PJ),
// mirroring how ProfilePage composes them.

import { useTranslation } from 'react-i18next';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
} from '@alamo/ui';

import { AddressSection } from '../../pages/Profile/components/AddressSection';
import { BankAccountSection } from '../../pages/Profile/components/BankAccountSection';
import { ContactSection } from '../../pages/Profile/components/ContactSection';
import { OrganizationSection } from '../../pages/Profile/components/OrganizationSection';
import { PersonSection } from '../../pages/Profile/components/PersonSection';

interface DataReconfirmationModalProps {
  /** When false the modal renders nothing (the provider gates this). */
  open: boolean;
  /** Stamp the confirmation (POST /profile/confirm-data) and close. */
  onConfirm: () => void;
  /** True while the confirm mutation is in flight. */
  isConfirming: boolean;
}

export function DataReconfirmationModal({
  open,
  onConfirm,
  isConfirming,
}: DataReconfirmationModalProps): JSX.Element | null {
  const { t } = useTranslation(['auth', 'common']);

  if (!open) return null;

  return (
    <AlertDialog open>
      <AlertDialogContent className="w-[calc(100%-2rem)] max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {t('auth:profile.reconfirmation.title', {
              defaultValue: 'Confirme seus dados cadastrais',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {t('auth:profile.reconfirmation.description', {
              defaultValue:
                'Revise seus dados abaixo. Corrija o que estiver desatualizado e clique em "Confirmar dados" para continuar.',
            })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* Reuse the self-service Profile sections. Each self-gates on its own
            GET (Person for PF/CLT, Organization for PJ) and renders the document
            read-only, so nothing extra is needed here to branch PF vs PJ. */}
        <div className="my-4 space-y-6">
          <PersonSection />
          <OrganizationSection />
          <ContactSection />
          <AddressSection />
          <BankAccountSection />
        </div>

        <div className="mt-2 flex items-center justify-end">
          {/* No Cancel/close — the modal is blocking until the user confirms. */}
          <AlertDialogAction
            onClick={(e) => {
              // Keep the modal open; the provider closes it after the mutation
              // + refreshUser() resolve. Radix would otherwise try to close it.
              e.preventDefault();
              onConfirm();
            }}
            disabled={isConfirming}
          >
            <Button variant="primary" isLoading={isConfirming} className="min-h-[44px]">
              {t('auth:profile.reconfirmation.confirm', { defaultValue: 'Confirmar dados' })}
            </Button>
          </AlertDialogAction>
        </div>
      </AlertDialogContent>
    </AlertDialog>
  );
}
